import {
  DeleteObjectCommand,
  GetObjectCommand,
  ListObjectsV2Command,
  PutObjectCommand,
  S3Client,
} from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";

export interface S3Credentials {
  accessKeyId: string;
  secretAccessKey: string;
  endpoint: string;
  region: string;
  bucketName: string;
  useCorsProxy?: boolean;
}

export class ClientS3Manager {
  private s3Client: S3Client;
  private credentials: S3Credentials;
  private normalizedEndpoint: string;

  constructor(credentials: S3Credentials) {
    this.credentials = credentials;

    // Initialize with original endpoint first
    this.normalizedEndpoint = this.normalizeEndpoint(credentials.endpoint);

    this.s3Client = new S3Client({
      endpoint: this.normalizedEndpoint,
      region: credentials.region,
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
      },
      forcePathStyle: this.shouldUsePathStyle(credentials.endpoint),
      requestHandler: {
        httpOptions: {
          timeout: 10000, // 10 second timeout
        },
      },
    });
  }

  private normalizeEndpoint(endpoint: string): string {
    // Remove trailing slash for consistency
    let normalized = endpoint.trim();
    if (normalized.endsWith("/")) {
      normalized = normalized.slice(0, -1);
    }

    // For MinIO, ensure we're using the correct protocol
    if (normalized.includes("360creators.com") || normalized.includes("minio")) {
      // If it's HTTP, we might need to handle CORS differently
      if (normalized.startsWith("http://")) {
        console.warn("Using HTTP for MinIO - ensure CORS is properly configured");
      }
    }

    return normalized;
  }

  private shouldUsePathStyle(endpoint: string): boolean {
    // Use path-style for all providers except AWS and DigitalOcean
    return !endpoint.includes("amazonaws.com") && !endpoint.includes("digitaloceanspaces.com");
  }

  async testConnection(): Promise<{ success: boolean; error?: string }> {
    try {
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Connection timeout")), 10000); // 10 second timeout
      });

      const testPromise = this.s3Client.send(
        new ListObjectsV2Command({
          Bucket: this.credentials.bucketName,
          MaxKeys: 1,
        }),
      );

      await Promise.race([testPromise, timeoutPromise]);
      return { success: true };
    } catch (error: unknown) {
      // Provide more specific error messages for common issues
      const baseMessage =
        error instanceof Error ? error.message : typeof error === "string" ? error : "";
      let errorMessage = baseMessage || "Connection failed";

      // Check for timeout
      if (errorMessage.includes("timeout")) {
        errorMessage =
          "Connection Timeout: The S3 server did not respond within 10 seconds. Please check your CORS configuration on your Hetzner bucket.";
      }

      // Check for CORS errors
      if (
        errorMessage.includes("CORS") ||
        errorMessage.includes("cors") ||
        errorMessage.includes("preflight")
      ) {
        if (
          this.credentials.endpoint.includes("your-objectstorage.com") ||
          this.credentials.endpoint.includes("hetzner") ||
          this.credentials.endpoint.includes("fsn1.") ||
          this.credentials.endpoint.includes("nbg1.") ||
          this.credentials.endpoint.includes("hel1.")
        ) {
          errorMessage =
            "CORS Error: Please configure CORS on your Hetzner bucket to allow requests from your domain. See the CORS configuration guide in the connection dialog.";
        } else {
          errorMessage =
            "CORS Error: The S3 server is not configured to allow requests from this domain. Please configure CORS on your server.";
        }
      }

      // Check for network errors
      if (errorMessage.includes("fetch") || errorMessage.includes("network")) {
        errorMessage =
          "Network Error: Unable to reach the S3 server. Please check the endpoint URL and ensure the server is running.";
      }

      // Check for authentication errors
      if (
        errorMessage.includes("InvalidAccessKeyId") ||
        errorMessage.includes("SignatureDoesNotMatch")
      ) {
        errorMessage =
          "Authentication Error: Invalid access key or secret key. Please check your credentials.";
      }

      // Check for bucket not found
      if (errorMessage.includes("NoSuchBucket")) {
        errorMessage =
          "Bucket Error: The specified bucket does not exist. Please check the bucket name.";
      }

      return { success: false, error: errorMessage };
    }
  }

  async uploadFile(file: File, path: string): Promise<string> {
    // For certain endpoints (like MinIO/custom S3), direct uploads often fail due to CORS
    // Skip direct upload attempts for endpoints known to have CORS issues
    //
    // WHY ERR_FAILED OCCURS:
    // 1. Browser tries to make a direct PUT request to S3 endpoint
    // 2. S3 server doesn't have CORS configured to allow PUT from browser origin
    // 3. Browser blocks the request and logs ERR_FAILED (this happens BEFORE our catch block)
    // 4. AWS SDK logs "non-retryable streaming request" warning
    //
    // SOLUTION: Skip direct uploads for problematic endpoints and use signed URLs instead
    // Signed URLs work around CORS because they're pre-authorized by the server
    const skipDirectUpload =
      this.normalizedEndpoint.includes("360creators.com") ||
      this.normalizedEndpoint.includes("minio") ||
      this.normalizedEndpoint.includes("localhost");

    if (skipDirectUpload) {
      // For known problematic endpoints, skip direct upload and use signed URL directly
      // This prevents console errors from failed direct upload attempts
      return await this.uploadFileViaSignedUrl(file, path);
    }

    // For other endpoints, try direct upload first but handle failures gracefully
    try {
      const stream = file.stream();

      const command = new PutObjectCommand({
        Bucket: this.credentials.bucketName,
        Key: path,
        Body: stream,
        ContentType: file.type,
      });

      // Use a timeout to prevent hanging
      const uploadPromise = this.s3Client.send(command);
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error("Upload timeout")), 5000);
      });

      await Promise.race([uploadPromise, timeoutPromise]);
      return path;
    } catch (error: unknown) {
      // Check if it's a network/CORS error that we should handle silently
      const errorMessage =
        error instanceof Error ? error.message : typeof error === "string" ? error : "";
      const errorName = error instanceof Error ? error.name : "";
      const errorMetadata =
        typeof error === "object" && error && "$metadata" in error
          ? (error as { $metadata?: { httpStatusCode?: number } }).$metadata
          : undefined;
      const isNetworkError =
        errorMessage.includes("Failed to fetch") ||
        errorMessage.includes("CORS") ||
        errorMessage.includes("ERR_FAILED") ||
        errorMessage.includes("non-retryable streaming request") ||
        errorMessage.includes("timeout") ||
        errorName === "NetworkingError" ||
        errorName === "TimeoutError" ||
        (errorMetadata && errorMetadata.httpStatusCode === undefined);

      if (isNetworkError) {
        // Silently fall back to signed URL upload - this is expected for many S3 setups
        return await this.uploadFileViaSignedUrl(file, path);
      }
      throw error;
    }
  }

  private async uploadFileViaSignedUrl(file: File, path: string): Promise<string> {
    // Generate signed URL for upload
    const uploadUrl = await this.generateUploadUrl(path, file.type);

    // Upload using fetch with signed URL
    const response = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": file.type,
      },
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }

    return path;
  }

  async generateUploadUrl(
    path: string,
    contentType: string,
    expiresIn: number = 86400,
  ): Promise<string> {
    const command = new PutObjectCommand({
      Bucket: this.credentials.bucketName,
      Key: path,
      ContentType: contentType,
    });

    return await getSignedUrl(this.s3Client, command, { expiresIn });
  }

  async generateDownloadUrl(path: string, expiresIn: number = 86400): Promise<string> {
    try {
      const command = new GetObjectCommand({
        Bucket: this.credentials.bucketName,
        Key: path,
      });

      const signedUrl = await getSignedUrl(this.s3Client, command, { expiresIn });

      // Log details for debugging (reduced logging)
      // console.log('🔗 Generated signed URL:', {
      //   bucket: this.credentials.bucketName,
      //   path,
      //   endpoint: this.credentials.endpoint,
      //   expiresIn,
      //   urlLength: signedUrl.length
      // });

      return signedUrl;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : "Unknown error";
      console.error("❌ Failed to generate signed URL:", {
        error: errorMessage,
        bucket: this.credentials.bucketName,
        path,
        endpoint: this.credentials.endpoint,
      });

      // Provide more specific error messages
      if (errorMessage.includes("InvalidAccessKeyId")) {
        throw new Error(
          `Invalid S3 Access Key ID. Please verify your credentials in craftspace settings.`,
        );
      } else if (errorMessage.includes("SignatureDoesNotMatch")) {
        throw new Error(
          `S3 signature mismatch. This could be due to incorrect secret key or clock synchronization issues.`,
        );
      } else if (errorMessage.includes("NetworkingError")) {
        throw new Error(
          `Cannot connect to S3 endpoint: ${this.credentials.endpoint}. Please check your connection and endpoint URL.`,
        );
      }

      throw error;
    }
  }

  async deleteFile(path: string): Promise<void> {
    const command = new DeleteObjectCommand({
      Bucket: this.credentials.bucketName,
      Key: path,
    });

    await this.s3Client.send(command);
  }

  async listFiles(prefix: string): Promise<string[]> {
    const command = new ListObjectsV2Command({
      Bucket: this.credentials.bucketName,
      Prefix: prefix,
    });

    const response = await this.s3Client.send(command);
    return response.Contents?.map((obj) => obj.Key).filter((key): key is string => !!key) || [];
  }

  async fileExists(path: string): Promise<boolean> {
    try {
      await this.s3Client.send(
        new GetObjectCommand({
          Bucket: this.credentials.bucketName,
          Key: path,
        }),
      );
      return true;
    } catch (error: unknown) {
      if (error instanceof Error && error.name === "NoSuchKey") {
        return false;
      }
      throw error;
    }
  }

  async getFileSize(path: string): Promise<number> {
    try {
      const response = await this.s3Client.send(
        new GetObjectCommand({
          Bucket: this.credentials.bucketName,
          Key: path,
        }),
      );
      return response.ContentLength || 0;
    } catch (error: unknown) {
      if (error instanceof Error && error.name === "NoSuchKey") {
        return 0;
      }
      throw error;
    }
  }
}
